import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AlumnoLayout from "@/components/AlumnoLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Filter, MessageSquare, User, Calendar, ChevronRight,
} from "lucide-react";

function fmtRel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "hace unos segundos";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `hace ${Math.floor(diff / 86400)} d`;
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}

const TYPES = [
  { value: "both", label: "Todos" },
  { value: "thread", label: "Solo temas" },
  { value: "post", label: "Solo respuestas" },
];
const SORTS = [
  { value: "relevance", label: "Relevancia" },
  { value: "recent", label: "Recientes" },
];
const RANGES = [
  { value: "", label: "Todo el tiempo" },
  { value: "7", label: "Últimos 7 días" },
  { value: "30", label: "Último mes" },
  { value: "90", label: "Últimos 90 días" },
];

export default function ForoBuscar() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [programSlug, setProgramSlug] = useState(sp.get("program_slug") || "");
  const [type, setType] = useState(sp.get("type") || "both");
  const [sort, setSort] = useState(sp.get("sort") || "relevance");
  const [range, setRange] = useState(sp.get("range") || "");
  const [author, setAuthor] = useState(sp.get("author") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [programs, setPrograms] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    api.get("/catalog/programs?limit=200").then((r) => {
      setPrograms(r.data.items || []);
    }).catch(() => {});
  }, []);

  const doSearch = (query = q) => {
    if (!query || query.trim().length < 2) {
      setData({ items: [], total: 0, query, took_ms: 0 });
      return;
    }
    setLoading(true);
    const params = new URLSearchParams({ q: query, type, sort, limit: "20" });
    if (programSlug) params.set("program_slug", programSlug);
    if (author) params.set("author", author);
    if (range) params.set("range", range);
    setSp(params);
    api.get(`/forums/search?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  const onInput = (v) => {
    setQ(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(v), 500);
  };

  useEffect(() => {
    if (q && q.trim().length >= 2) doSearch(q);
    // eslint-disable-next-line
  }, [type, sort, range, programSlug, author]);

  useEffect(() => {
    if (sp.get("q")) doSearch(sp.get("q"));
    // eslint-disable-next-line
  }, []);

  const goResult = (it) => {
    if (it.kind === "thread") {
      nav(`/app/alumno/programas/${it.program_slug}/foro/thread/${it.id}`);
    } else if (it.kind === "post" && it.thread_id) {
      nav(`/app/alumno/programas/${it.program_slug}/foro/thread/${it.thread_id}#p-${it.id}`);
    }
  };

  return (
    <AlumnoLayout user={user}>
      <div data-testid="forum-search-page">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-brand-orange-500 font-semibold">
            Fase 14.5 · Foros
          </p>
          <h1 className="mt-2 text-3xl font-bold text-white font-display flex items-center gap-2">
            <Search className="w-7 h-7" /> Buscar en foros
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Encuentra respuestas en toda la comunidad
          </p>
        </div>

        {/* Search input */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
          <input
            value={q}
            onChange={(e) => onInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") doSearch(); }}
            data-testid="forum-search-input"
            placeholder="Busca temas y respuestas…"
            autoFocus
            className="w-full bg-[#111827] border border-gray-800 rounded-lg pl-11 pr-4 py-3 text-base text-white focus:outline-none focus:border-brand-orange-500"
          />
          {loading && (
            <div className="absolute right-3 top-3 text-xs text-brand-orange-500 animate-pulse">
              Buscando…
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="forum-search-filters">
          <SelectField label="Tipo" testid="filter-type" value={type} onChange={setType} options={TYPES} />
          <SelectField label="Orden" testid="filter-sort" value={sort} onChange={setSort} options={SORTS} />
          <SelectField label="Fecha" testid="filter-range" value={range} onChange={setRange} options={RANGES} />
          <SelectField
            label="Programa" testid="filter-program"
            value={programSlug} onChange={setProgramSlug}
            options={[{ value: "", label: "Todos los programas" },
                        ...programs.map((p) => ({ value: p.slug, label: `${p.code || ""} · ${p.title}`.trim() }))]}
          />
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold flex items-center gap-1">
              <User className="w-3 h-3" /> Autor
            </label>
            <input
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="email exacto"
              data-testid="filter-author"
              className="mt-1 bg-[#111827] border border-gray-800 rounded-md px-3 py-1.5 text-xs text-white font-mono w-48"
            />
          </div>
        </div>

        {/* Results */}
        {!data ? (
          <div className="p-8 border border-dashed border-gray-800 rounded-xl text-center text-gray-500 text-sm">
            Escribe tu búsqueda arriba (min 2 caracteres).
          </div>
        ) : data.items.length === 0 && q.trim().length >= 2 ? (
          <div className="p-8 border border-dashed border-gray-800 rounded-xl text-center" data-testid="forum-search-empty">
            <MessageSquare className="w-8 h-8 mx-auto text-gray-500" />
            <p className="mt-3 text-white font-semibold">Sin resultados</p>
            <p className="mt-1 text-sm text-gray-400">Prueba con otras palabras o cambia los filtros.</p>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-500 font-mono mb-3" data-testid="forum-search-summary">
              {data.total} resultado{data.total === 1 ? "" : "s"} en {data.took_ms}ms
              {q && <> · <span className="text-brand-orange-500">"{q}"</span></>}
            </p>
            <div className="space-y-3" data-testid="forum-search-results">
              {data.items.map((it) => (
                <button
                  key={`${it.kind}-${it.id}`}
                  type="button"
                  onClick={() => goResult(it)}
                  data-testid={`forum-search-result-${it.id}`}
                  className="w-full text-left p-4 bg-[#111827] border border-gray-800 rounded-xl hover:border-brand-orange-500/60 transition-colors group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                          it.kind === "thread"
                            ? "bg-brand-orange-500/15 border-brand-orange-500/40 text-brand-orange-500"
                            : "bg-blue-500/15 border-blue-500/40 text-blue-300"
                        }`}>
                          {it.kind === "thread" ? "Tema" : "Respuesta"}
                        </span>
                        {it.program_slug && (
                          <span className="text-[10px] text-gray-500 font-mono">{it.program_slug}</span>
                        )}
                      </div>
                      {it.title && (
                        <h3 className="text-base font-semibold text-white group-hover:text-brand-orange-500 mb-1">
                          {it.title}
                        </h3>
                      )}
                      {it.thread_title && it.kind === "post" && (
                        <p className="text-xs text-gray-400 mb-1">
                          en tema · {it.thread_title}
                        </p>
                      )}
                      <p
                        className="text-sm text-gray-300 [&_em]:bg-brand-orange-500/30 [&_em]:text-white [&_em]:not-italic [&_em]:px-0.5 [&_em]:rounded"
                        dangerouslySetInnerHTML={{ __html: it.highlight || "" }}
                      />
                      <div className="mt-2 flex items-center gap-2 text-[11px] text-gray-500">
                        <span><User className="w-3 h-3 inline mr-1" />{it.author_name}</span>
                        <span><Calendar className="w-3 h-3 inline mr-1" />{fmtRel(it.created_at)}</span>
                        {it.reactions_summary && (
                          <span>· 👍 {it.reactions_summary.like || 0} ❤️ {it.reactions_summary.love || 0}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end text-xs">
                      <span className="text-brand-orange-500 font-mono font-bold">
                        {it.score.toFixed(2)}
                      </span>
                      <span className="text-[10px] text-gray-500">score</span>
                      <ChevronRight className="w-4 h-4 mt-2 text-gray-500 group-hover:text-brand-orange-500" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </AlumnoLayout>
  );
}

function SelectField({ label, testid, value, onChange, options }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold flex items-center gap-1">
        <Filter className="w-3 h-3" /> {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 bg-[#111827] border border-gray-800 rounded-md px-3 py-1.5 text-xs text-white"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}
